import { CheckIcon, XIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from 'components/ui/chart'
import { Switch } from 'slab'

import { type BucketData } from './types'
import {
  DARK_GREEN_COLOR,
  DARK_RED_COLOR,
  GREEN_COLOR,
  RED_COLOR,
} from './utils'

const CHART_CONFIG = {
  count: {
    label: 'Employees',
  },
  payingMore: {
    label: 'Paying More',
    color: RED_COLOR,
  },
  savingMoney: {
    label: 'Saving Money',
    color: GREEN_COLOR,
  },
} as const

type CostImpactBarChartProps = {
  buckets: BucketData[]
  onBucketClick: (bucket: BucketData) => void
}

export default function CostImpactBarChart ({ buckets, onBucketClick }: CostImpactBarChartProps) {
  const [viewMode, setViewMode] = useState<'count' | 'percent'>('count')

  // Calculate total employees for percentage calculations
  const totalEmployees = useMemo(() => {
    return buckets.reduce((sum, bucket) => sum + bucket.count, 0)
  }, [buckets])

  // Transform data based on view mode
  const chartData = useMemo(() => {
    if (viewMode === 'percent' && totalEmployees > 0) {
      return buckets.map(bucket => ({
        ...bucket,
        percentage: (bucket.count / totalEmployees) * 100,
      }))
    }
    return buckets
  }, [buckets, viewMode, totalEmployees])

  const handleBarClick = (_data: unknown, index: number) => {
    const bucket = buckets[index]
    if (bucket && bucket.count > 0) {
      onBucketClick(bucket)
    }
  }

  // Custom shape that includes an invisible full-height clickable area
  const BarWithClickableArea = (props: any) => {
    const { payload, index, x, width, y, height } = props
    const bucket = buckets[index]

    if (!bucket || bucket.count === 0) {
      return <g />
    }

    // Create an invisible rectangle that spans the full chart height
    // Use a very large height value - it will be clipped by the chart's boundaries
    // This makes the entire vertical area clickable, not just the bar itself
    return (
      <g>
        {/* Invisible full-height clickable rectangle - spans entire vertical area */}
        <rect
          x={x}
          y={0}
          width={width}
          height={10000}
          fill='transparent'
          style={{ cursor: 'pointer' }}
          onClick={() => handleBarClick(payload, index)}
        />
        {/* Visible bar with gradient and styling */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={4}
          ry={4}
          fill={bucket.isSavings ? 'url(#greenBevel)' : 'url(#redBevel)'}
          stroke={bucket.isSavings ? DARK_GREEN_COLOR : DARK_RED_COLOR}
          strokeWidth={1}
          style={{ cursor: 'pointer' }}
          onClick={() => handleBarClick(payload, index)}
          className='hover:opacity-80 rounded-t-lg transition-opacity'
        />
      </g>
    )
  }

  return (
    <div className='relative w-full'>
      <div className='flex items-center justify-end gap-2 mb-2'>
        <Switch
          onChange={() => setViewMode(viewMode === 'count' ? 'percent' : 'count')}
          label='Display as %'
          checkedIcon={CheckIcon}
          uncheckedIcon={XIcon}
        />
      </div>
      <ChartContainer config={CHART_CONFIG} className='h-64 md:h-96 w-full'>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 20, bottom: 60, left: 40 }}
        >
          <defs>
            <linearGradient id='greenBevel' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={GREEN_COLOR} />
              <stop offset='100%' stopColor={DARK_GREEN_COLOR} />
            </linearGradient>
            <linearGradient id='redBevel' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={RED_COLOR} />
              <stop offset='100%' stopColor={DARK_RED_COLOR} />
            </linearGradient>
            <filter id='barShadow' x='-5%' y='-5%' width='110%' height='115%'>
              <feDropShadow dx='0' dy='1' stdDeviation='1' floodColor='#E5E5E5' floodOpacity='0.15' />
            </filter>
          </defs>
          <XAxis
            dataKey='range'
            tickLine={false}
            axisLine={false}
            angle={-45}
            textAnchor='end'
            height={80}
            interval={0}
            tickSize={10}
            tick={{ fontSize: 11 }}
            tickFormatter={(_value, index) => buckets[index]?.range ?? ''}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            allowDecimals={viewMode === 'percent'}
            tick={{ fontSize: 12 }}
            tickFormatter={viewMode === 'percent' ? (value) => `${value}%` : undefined}
            label={{
              value: viewMode === 'percent' ? 'Percent of Employees' : 'Employees',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: 12, fill: '#6b7280' },
            }}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                hideLabel
                hideIndicator
                formatter={(value, _name, item) => {
                  const bucket = item.payload as BucketData & { percentage?: number }
                  const displayValue = viewMode === 'percent'
                    ? `${bucket.percentage?.toFixed(1) ?? 0}% of employees`
                    : `${value} employee${value === 1 ? '' : 's'}`
                  return (
                    <div className='flex flex-col gap-1'>
                      <span className='font-medium'>{displayValue}</span>
                      <span className='text-gray-500 text-xs'>
                        {bucket.isSavings ? 'Saving' : 'Paying'} {bucket.range}
                        {bucket.isSavings ? ' per month' : ' more per month'}
                      </span>
                    </div>
                  )
                }}
              />
            }
          />
          <CartesianGrid strokeDasharray='3 3' stroke='#e9e6e6' />
          <Bar
            dataKey={viewMode === 'percent' ? 'percentage' : 'count'}
            shape={BarWithClickableArea}
            className='cursor-pointer'
            filter='url(#barShadow)'
          />
        </BarChart>
      </ChartContainer>
    </div>
  )
}
